import { createSelector } from 'reselect';
import isNil from 'lodash/isNil';
import { touch_object } from '../../../../../js/app/react/helpers.objects';
import { LifeInsuranceProgressChartBarChart } from '../../../../../js/app/react/chartDescriptors.financialPlan';
import { computeChartStringKey, computeChartIndex } from './ProgressChartHelpers';

import { selectedIndex as selectedOwnerIndex } from '../../OwnerDropdown/OwnerDropdownSelector';
import {
  selectedTabId,
  selectedIndex as selectedTabIndex,
} from '../../SplitTabs/SplitTabsSelector';

const BASE_LOCAL_DATA = {
  localChartData: null,
  localGroup: null,
  localGoalData: null,
  localResponseData: null,
};

const dictionary = {};

const renderChartLabels = key =>
  // eslint-disable-next-line func-names
  function() {
    if (dictionary[key].localGroup) {
      dictionary[key].localGroup.destroy();
    }

    dictionary[key].localGroup = this.renderer
      .g()
      .attr({
        zIndex: 10,
        id: 'chart-labels',
      })
      .add();

    const { Current, Recommended } = dictionary[key].localGoalData;

    const center = this.renderer.height / 2.0;
    const offset = 35;

    const YOFFSETS = [center + offset, center - offset];

    const CARET_DIMENSIONS = {
      x: 15.6,
      y: 25,
    };

    const TEXT_DIMENSIONS = {
      y: 40,
    };

    /* CURRENT LABEL */
    if (Current[0] > 0) {
      const CURRENT_LABEL_POINT = {
        x: this.yAxis[0].toPixels(Current[0]),
        y: YOFFSETS[0],
      };

      const CURRENT_LABEL_ALIGNMENT = CURRENT_LABEL_POINT.x < 100 ? 'left' : 'right';

      // Render the caret
      this.renderer
        .text(
          '\uf0d8', // fa-caret-up
          CURRENT_LABEL_POINT.x + CARET_DIMENSIONS.x / 2,
          CURRENT_LABEL_POINT.y + CARET_DIMENSIONS.y,
        )
        .attr({ align: 'right' })
        .css({
          fontFamily: '"Font Awesome 5 Pro"',
          fontWeight: '900',
          fontSize: '25px',
          color: Current[0] < Recommended[0] ? '#999' : '#5abee4',
        })
        .add(dictionary[key].localGroup);

      // Render the label
      const AMOUNT = `$${ADV.util.number_format(Current[0])}`;
      this.renderer
        .text(
          `CURRENT <br><span style="font-weight:700;font-size:17px;">${AMOUNT}</span>`,
          CURRENT_LABEL_ALIGNMENT === 'right'
            ? CURRENT_LABEL_POINT.x + CARET_DIMENSIONS.x / 2
            : CURRENT_LABEL_POINT.x - CARET_DIMENSIONS.x / 2,
          CURRENT_LABEL_POINT.y + TEXT_DIMENSIONS.y,
        )
        .attr({ align: CURRENT_LABEL_ALIGNMENT })
        .css({
          fontSize: '12px',
          fontWeight: '400',
          color: Current[0] < Recommended[0] ? '#999' : '#5abee4',
          fontFamily: 'Open Sans, helvetica, sans-serif',
          lineHeight: '17px',
        })
        .add(dictionary[key].localGroup);
    }

    /* RECOMMENDED LABEL */
    const RECOMMENDED_LABEL_POINT = {
      x: this.yAxis[0].toPixels(Recommended[0]),
      y: YOFFSETS[1],
    };

    const RECOMMENDED_LABEL_ALIGNMENT = RECOMMENDED_LABEL_POINT.x < 100 ? 'left' : 'right';

    // Render the caret
    this.renderer
      .text(
        '\uf0d7', // fa-caret-down
        RECOMMENDED_LABEL_POINT.x + CARET_DIMENSIONS.x / 2,
        RECOMMENDED_LABEL_POINT.y - CARET_DIMENSIONS.y,
      )
      .attr({ align: 'right' })
      .css({
        fontFamily: '"Font Awesome 5 Pro"',
        fontSize: '25px',
        fontWeight: '900',
        color: '#5abee4',
      })
      .add(dictionary[key].localGroup);

    // Render the label
    const RECOMMENDED_AMOUNT = `$${ADV.util.number_format(Recommended[0])}`;
    this.renderer
      .text(
        `RECOMMENDED<br><span style="font-weight:700;font-size:18px;">${RECOMMENDED_AMOUNT}</span>`,
        RECOMMENDED_LABEL_ALIGNMENT === 'right'
          ? RECOMMENDED_LABEL_POINT.x + CARET_DIMENSIONS.x / 2
          : RECOMMENDED_LABEL_POINT.x - CARET_DIMENSIONS.x / 2,
        RECOMMENDED_LABEL_POINT.y - TEXT_DIMENSIONS.y - CARET_DIMENSIONS.y,
      )
      .attr({ align: RECOMMENDED_LABEL_ALIGNMENT })
      .css({
        fontSize: '13px',
        fontWeight: '400',
        color: '#5abee4',
        fontFamily: 'Open Sans, helvetica, sans-serif',
        lineHeight: '18px',
      })
      .add(dictionary[key].localGroup);
  };

export const responseDataSelector = (state, props) => {
    const tabId = selectedTabId(state, props),
      tabIndex = selectedTabIndex(state, props),
      ownerIndex = !isNil(props.ownerIndex) ? props.ownerIndex : selectedOwnerIndex(state, props),
      chartIndex = computeChartIndex(ownerIndex === 1, props.tabId || tabId);

    return {
      ...props,
      ownerIndex,
      tabId: props.tabId || tabId,
      tabIndex,
      chartIndex,
    };
  },
  chartDataSelector = createSelector(
    [
      (state, props) => {
        const responseData = responseDataSelector(state, props),
          data = responseData.subgoalData.GoalCharts[responseData.chartIndex];
        return {
          responseData: data,
          isPrint: props.isPrint,
          key: computeChartStringKey(
            responseData.ownerIndex.toString(),
            `${props.subgoalId}-${responseData.tabIndex}`,
            props.fpId,
            props.clientId,
          ),
        };
      },
    ],
    ({ responseData, isPrint, key }) => {
      if (responseData == null || Object.keys(responseData).length < 1) {
        return null;
      }

      if (!dictionary[key]) {
        dictionary[key] = clone(BASE_LOCAL_DATA);
      }

      if (ADV.util.deepEqual(responseData, dictionary[key].localResponseData)) {
        return dictionary[key].localChartData;
      }

      dictionary[key].localGoalData = {
        Recommended: [responseData.Data.Series.Recommended],
        Current: [responseData.Data.Series.Current],
      };

      let chartData = clone(LifeInsuranceProgressChartBarChart);

      touch_object(chartData, 'xAxis');
      chartData.xAxis.categories = ['Fund Size'];

      chartData.series[0].data = dictionary[key].localGoalData.Recommended;
      chartData.series[1].data = dictionary[key].localGoalData.Current;

      if (responseData.Data.IsEmpty) {
        chartData.yAxis.max = 10000;
      }

      if (isPrint) {
        chartData.series.forEach(serie => {
          serie.animation = false;
        });
      }

      chartData.chart.events = {
        load: renderChartLabels(key),
        redraw: renderChartLabels(key),
      };
      dictionary[key].localResponseData = responseData;
      dictionary[key].localChartData = chartData;

      return chartData;
    },
  );
